import Cookies from 'js-cookie';
import * as jwt from 'jsonwebtoken';
import { InvalidTokenError, jwtDecode } from 'jwt-decode';

import { getApplyUuid, setApplyUuid } from '@consumer-frontend/nc-utils';

export const ACCESS_TOKEN_KEY: string = import.meta.env.VITE_NC_ACCESS_TOKEN_KEY || 'netcredit_access_tokens';
export const REFRESH_TOKEN_KEY: string = import.meta.env.VITE_NC_REFRESH_TOKEN_KEY || 'netcredit_refresh_tokens';
export const NETCREDIT_SESSION_KEY = '_netcredit_session';
export const SIGNIN_VERIFICATION_CODE_KEY = 'signin_verification_code';

const SIGNIN_VERIFICATION_CODE_EXPIRATION_MINUTES = 10;

type LoginServerPayloadT = jwt.JwtPayload & {
  authorized?: boolean;
  client_id?: string;
  tenant_id?: string;
  user_id?: string;
};
type DecodedTokenT = [LoginServerPayloadT | undefined, Error | undefined];

// Cookies env var helpers
const cookiesDomain = () => import.meta.env.VITE_NC_COOKIES_DOMAIN || '.netcredit.dev';
const cookiesSecure = () => import.meta.env.VITE_NC_COOKIES_SECURE === 'true';
// This is needed when setting or deleting cookies. It's not needed when getting cookies.
const defaultCookieAttrs = (): Cookies.CookieAttributes => ({
  domain: cookiesDomain(),
  path: '/',
  secure: cookiesSecure(),
});
const defaultSessionAttrs = (): Cookies.CookieAttributes => ({
  domain: cookiesDomain(),
  httpOnly: true,
  path: '/',
  secure: false,
});
// Staging cookie domain edge case helpers
const stagingOrUatSubstr = /staging\.|uat\./;
const isStagingOrUodEnv = () => cookiesDomain().match(stagingOrUatSubstr);
const additionalDomain = () => cookiesDomain().replace(stagingOrUatSubstr, '');

type CookieJarT = {
  expire: () => void;
  getAccessToken: () => string;
  getAccountId: () => [string | undefined, Error | undefined];
  getApplyUuid: () => string;
  getDebugDump: () => {
    timestamps: {
      now: string;
      expiration?: string;
      issuedAt?: string;
      notBefore?: string;
    };
    accessToken?: string;
    accessTokenError?: string;
    refreshToken?: string;
    refreshTokenError?: string;
  };
  getParsedTokens: () => {
    accessToken: DecodedTokenT;
    refreshToken: DecodedTokenT;
  };
  getRawAccessToken: () => string;
  getRawRefreshToken: () => string;
  getRefreshToken: () => string;
  getSigninVerificationCode: () => string | undefined;
  isTokenExpired: () => boolean;
  removeSigninVerificationCode: () => void;
  saveTokens: (accessToken: string, refreshToken: string) => void;
  setApplyUuid: () => string;
  setSigninVerificationCode: () => string;
};

// The JSON bits are necessary because of the way the tokens are created in account_home.
// Cf: https://git.enova.com/netcredit/account_home/blob/main/app/helpers/cookie_helper.rb
// The `window.` bits are necessary because otherwise TS/VSCode think you're trying to use the deprecated `btoa` and `atob`
// functions from Node rather than the non-deprecated versions from the browser.
export const decodeBase64 = (token: string): string => {
  if (!token) return '';

  return JSON.parse(window.atob(token));
};

export const encodeBase64 = (token: string): string => {
  if (!token) return '';

  return window.btoa(JSON.stringify(token));
};

export const parseJwt = (token: string): [LoginServerPayloadT | undefined, Error | undefined] => {
  try {
    const decodedToken = jwtDecode<jwt.JwtPayload>(token);

    return [decodedToken as LoginServerPayloadT, undefined];
  } catch (err) {
    return [undefined, new Error(`Error decoding token: ${(err as InvalidTokenError)?.message}`)];
  }
};

export const cookieJar: CookieJarT = {
  expire: () => {
    Cookies.remove(ACCESS_TOKEN_KEY, defaultCookieAttrs());
    Cookies.remove(REFRESH_TOKEN_KEY, defaultCookieAttrs());
    Cookies.remove(NETCREDIT_SESSION_KEY, defaultSessionAttrs());

    // This shouldn't happen anymore, but leaving as a safeguard just in case.
    if (isStagingOrUodEnv()) {
      const secondDomain = additionalDomain();

      Cookies.remove(ACCESS_TOKEN_KEY, { ...defaultCookieAttrs(), domain: secondDomain });
      Cookies.remove(REFRESH_TOKEN_KEY, { ...defaultCookieAttrs(), domain: secondDomain });
      Cookies.remove(NETCREDIT_SESSION_KEY, { ...defaultSessionAttrs(), domain: secondDomain });
    }
  },
  getAccessToken: () => decodeBase64(Cookies.get(ACCESS_TOKEN_KEY) ?? ''),
  getAccountId: () => {
    const accessToken = cookieJar.getAccessToken();

    if (!accessToken) {
      if (import.meta.env.VITE_MOCKING === 'true' && import.meta.env.VITE_ENV === 'development') {
        return ['123456', undefined];
      }

      return [undefined, new Error('No access token found')];
    }

    const [parsedToken, error] = parseJwt(accessToken);

    return [parsedToken?.user_id, error];
  },
  getApplyUuid,
  getDebugDump: () => {
    const { accessToken, refreshToken } = cookieJar.getParsedTokens();
    const [accessTokenPayload, accessTokenErrorPayload] = accessToken;
    const [refreshTokenPayload, refreshTokenErrorPayload] = refreshToken;

    return {
      accessToken: JSON.stringify(accessTokenPayload, null, 2),
      accessTokenError: JSON.stringify(accessTokenErrorPayload, null, 2),
      refreshToken: JSON.stringify(refreshTokenPayload, null, 2),
      refreshTokenError: JSON.stringify(refreshTokenErrorPayload, null, 2),
      timestamps: {
        expiration: accessTokenPayload?.exp ? new Date(accessTokenPayload.exp * 1000).toLocaleString() : undefined,
        issuedAt: accessTokenPayload?.iat ? new Date(accessTokenPayload.iat * 1000).toLocaleString() : undefined,
        notBefore: accessTokenPayload?.nbf ? new Date(accessTokenPayload.nbf * 1000).toLocaleString() : undefined,
        now: new Date().toLocaleString(),
      },
    };
  },
  getParsedTokens: () => ({
    accessToken: parseJwt(cookieJar.getAccessToken()),
    refreshToken: parseJwt(cookieJar.getRefreshToken()),
  }),
  getRawAccessToken: () => Cookies.get(ACCESS_TOKEN_KEY) ?? '',
  getRawRefreshToken: () => Cookies.get(REFRESH_TOKEN_KEY) ?? '',
  getRefreshToken: () => decodeBase64(Cookies.get(REFRESH_TOKEN_KEY) ?? ''),
  getSigninVerificationCode: () => Cookies.get(SIGNIN_VERIFICATION_CODE_KEY),
  isTokenExpired: () => {
    const [parsedToken] = parseJwt(cookieJar.getAccessToken());

    if (!parsedToken) return true;

    // exp format: https://datatracker.ietf.org/doc/html/rfc7519#section-4.1.4
    return !parsedToken?.exp || Date.now() >= parsedToken.exp * 1000;
  },
  removeSigninVerificationCode: () => Cookies.remove(SIGNIN_VERIFICATION_CODE_KEY, defaultCookieAttrs()),
  saveTokens: (accessToken: string, refreshToken: string) => {
    Cookies.set(ACCESS_TOKEN_KEY, encodeBase64(accessToken), defaultCookieAttrs());
    Cookies.set(REFRESH_TOKEN_KEY, encodeBase64(refreshToken), defaultCookieAttrs());
  },
  setApplyUuid,
  setSigninVerificationCode: () => {
    const expires = new Date(
      new Date().getTime() + SIGNIN_VERIFICATION_CODE_EXPIRATION_MINUTES * 60 * 1000,
    );
    const signinVerificationCode = crypto.randomUUID();

    Cookies.set(
      SIGNIN_VERIFICATION_CODE_KEY,
      signinVerificationCode,
      {
        ...defaultCookieAttrs(),
        expires,
      },
    );

    return signinVerificationCode;
  },
};
